/**
 * Shared single-observer context for squad planning player list.
 *
 * useSquadPlanningPlayers is called independently by about 12 sibling
 * organisms under the Squad Planning screen (PlayerPanel, PlanningActionsBar,
 * ActionsSummaryPanel, ActionMenu, FinancialBar, SquadPlanningContainer,
 * ScenarioPillBar, AddAcademyPlayerModal, KpiArea, MobileActionsOverview,
 * useLiveImpact, and more). Before this fix each one ran its own independent
 * useSquadPlanningGetSquadPlayersByPositionQuery, its own Apollo
 * ObservableQuery / cache watcher over the exact same query and variables.
 *
 * SE-8191 original fix wrapped the firstTeam/academy/targets transform in a
 * per-consumer useMemo keyed on the query result. That only prevented
 * re-transforms on re-renders unrelated to the query (view toggles, modal
 * open/close, and so on). It did nothing for the very first render after a
 * scenario switch, when every one of those roughly 12 independent observers
 * necessarily re-diffs the cache and re-runs its own transform, all
 * synchronously, in the same tick. targets alone can run to several hundred
 * rows (the org full scouting-eligible pool, not scenario-scoped), so that
 * fan-out, both the InMemoryCache broadcast re-diffing every watcher and the
 * redundant re-transforms of the same rows, was measured to block the main
 * thread for seconds on a scenario switch and trip Chrome Page Unresponsive
 * dialog (QA kickback, SE-8191, retested 2026-09-13 against the SE-8191 and
 * SE-8205 fixes and still reproduced).
 *
 * This module fetches and transforms the player list exactly once, in a
 * single SquadPlanningPlayersProvider mounted above every consumer (the
 * Squad screen container), and distributes the already-computed result via
 * context. useSquadPlanningPlayers, the hook every call site already
 * imports, becomes a thin context read, so no call site code needed to
 * change.
 *
 * SE-8346 follow-up: SE-8191's fix collapsed the Apollo-observer fan-out
 * (12 watchers -> 1), but a second, downstream fan-out survived it. `targets`
 * carries the org's ENTIRE watchlist pool (427 rows measured live on
 * staging, SE-8346) — not scenario-scoped, not list-scoped — and every one
 * of those rows used to get put through {@link formatTargetPlayer} (full
 * SquadPlayer shape: display name, GPR, positions, every field) on every
 * scenario switch, purely because they rode along in the same `players`
 * array every consumer reads. Auditing every actual consumer (SE-8346)
 * found none of them need the full formatted shape for all 427: the
 * visible Targets panel is powered by an entirely separate query
 * (useSquadPlanningTargets / listAllScoutingTargets, ~71 rows); the depth
 * chart and KPI area explicitly filter Targets out before computing;  the
 * two remaining real needs (the scenario-pill "N acts" badge, and the
 * saved-scenario edit-mode seed) only ever read `id` and `transitionType`,
 * two raw fields, off every row. `players` below is now roster-only
 * (First Team + Academy) — no consumer's behavior depends on it including
 * Targets, confirmed by the SE-8346 audit. `rawTargets` exposes the
 * untransformed rows for exactly those two narrow, cheap reads, so nothing
 * pays for formatting 427 full player objects just to read two fields off
 * some of them.
 * @module features/squad-planning/contexts/SquadPlanningPlayersContext
 */

import { NetworkStatus } from "@apollo/client";
import React, { createContext, useContext, useMemo } from "react";

import type { TargetPositionPlayerModel } from "@/generated/graphql";
import { useSquadPlanningGetSquadPlayersByPositionQuery } from "@/generated/graphql";
import { useTeamSelection } from "@/providers/TeamSelectionProvider";

import type { SquadPlayer } from "../types";
import { useScenarios } from "../stores/scenarioStore";
import {
  formatAcademyPlayer,
  formatFirstTeamPlayer,
} from "../utils/formatPlayerRow";

/** Value shape shared by every useSquadPlanningPlayers consumer. */
export interface SquadPlanningPlayersContextValue {
  /**
   * Normalised ROSTER player list (First Team + Academy) ready for squad
   * planning components. Does NOT include Targets (SE-8346) — see
   * {@link rawTargets} and the module doc for why, and for where a
   * Targets-derived value is genuinely needed.
   */
  readonly players: readonly SquadPlayer[];
  /**
   * Raw, UNFORMATTED target rows straight from the query (SE-8346) — not
   * run through {@link formatTargetPlayer}. Only `id` and `transitionType`
   * are the sanctioned reads off this (the scenario-pill act count and the
   * saved-scenario edit-mode seed); anything needing a fully formatted
   * Targets-tier SquadPlayer should use useSquadPlanningTargets, which is
   * what actually powers the visible Targets panel.
   */
  readonly rawTargets: readonly TargetPositionPlayerModel[];
  /**
   * The Current Squad roster (First Team + Academy), whatever view is on
   * screen — `players` itself on Current Squad, a second, `no-cache` read
   * of `getSquadPlayersByPosition({ scenarioId: null })` inside a scenario
   * (SE-8773: the wage tile's "current" figure must equal the Current Squad
   * tile, which a scenario's own rows cannot give). Null until that read
   * resolves.
   */
  readonly baselinePlayers: readonly SquadPlayer[] | null;
  /** Whether the initial query is still loading (not refetch). */
  readonly loading: boolean;
  /**
   * A scenario switch is in flight: the query variables changed and the
   * network round-trip has not settled, while players still holds the
   * previous scenario rows so the panel can update in place (SE-5351).
   */
  readonly isSwitching: boolean;
  /** The error from the query, if any. */
  readonly error: Error | undefined;
  /** Re-fetches the player list from the network. */
  readonly refetch: () => void;
}

const SquadPlanningPlayersContext = createContext<
  SquadPlanningPlayersContextValue | undefined
>(undefined);

/** Props for SquadPlanningPlayersProvider. */
interface SquadPlanningPlayersProviderProps {
  /** Subtree that may call useSquadPlanningPlayers. */
  readonly children: React.ReactNode;
  /**
   * Skip the underlying network query while true.
   *
   * Before this fix, none of the roughly 12 consumers ever mounted outside
   * the Planning sub-tab (Contracts/Analysis/Compliance render entirely
   * different subtrees), so the query naturally never fired there. Now that
   * one Provider fetches independently of which consumer is on screen, it
   * has to reproduce that same gate itself: pass activeTab !== "planning"
   * from the Squad screen container.
   */
  readonly skip?: boolean;
}

/**
 * Fetches the organisation squad players via getSquadPlayersByPosition
 * (no position filter, returns all players; position filtering is applied
 * client-side, SE-4681), transforms them into the lightweight
 * SquadPlayer shape, and provides the result to every
 * useSquadPlanningPlayers call beneath it. See the module doc for why this
 * fetch and transform must happen exactly once.
 *
 * Uses cache-and-network so the UI shows cached data immediately while
 * refreshing in the background. The loading flag only reflects the initial
 * fetch; background refetches do not flip it to true, preventing
 * full-screen spinner flashes on mutation refetches.
 * @param props - Provider props
 * @param props.children - Subtree that may call useSquadPlanningPlayers
 * @param props.skip - Skip the query (see prop doc)
 * @returns The context provider wrapping children
 */
export function SquadPlanningPlayersProvider({
  children,
  skip = false,
}: SquadPlanningPlayersProviderProps) {
  const { activeId } = useScenarios();
  const { isLoaded: isTeamSelectionLoaded } = useTeamSelection();
  // SE-8205: NOT gated with skip: !isTeamSelectionLoaded. The POV team
  // header this query needs is attached by povTeamHeaderLink
  // (providers/povTeamHeaderLink.ts), which already defers forwarding any
  // operation until team selection has hydrated, the same mechanism
  // SquadPlanningListScenarios / ScoutingListAllTargets already rely on
  // with no skip at all. Gating dispatch here too was redundant with that
  // link-level defer, and cost a real extra sequential hop on cold load.
  // skip above is ONLY the tab-visibility gate (see prop doc), never
  // team-selection hydration.
  const { data, previousData, loading, error, refetch, networkStatus } =
    useSquadPlanningGetSquadPlayersByPositionQuery({
      variables: { scenarioId: activeId },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      skip,
    });

  // SE-5351: fall back to previousData during a scenario switch so the
  // panel keeps rendering the prior rows in place instead of unmounting
  // into a full-screen spinner (which read as a full page reload).
  const result = (data ?? previousData)?.getSquadPlayersByPosition;

  // SE-8191: computed ONCE here, not once per consumer. See module doc.
  // SE-8346: roster only (First Team + Academy) — targets are exposed raw
  // below, unformatted, since no consumer needs all 427 in full SquadPlayer
  // shape (see module doc).
  const players: readonly SquadPlayer[] = useMemo(
    () => [
      ...(result?.firstTeam ?? []).map(formatFirstTeamPlayer),
      ...(result?.academy ?? []).map(formatAcademyPlayer),
    ],
    [result]
  );

  // SE-8346: raw, unformatted — see module doc for the sanctioned reads.
  // Memoized on `result` alone (not a bare `?? []`) so the fallback empty
  // array is referentially stable too — otherwise it's a fresh array every
  // render whenever targets is absent, which would defeat the `value`
  // memo below exactly the way SE-8191 exists to prevent.
  const rawTargets: readonly TargetPositionPlayerModel[] = useMemo(
    () => result?.targets ?? [],
    [result]
  );

  // SE-8773: the Current Squad rows, for figures that compare a scenario
  // with Current Squad. `no-cache`, deliberately: `SquadPositionPlayerModel`
  // normalizes on `id` alone, so Current Squad's read and the scenario's read
  // of one player share ONE cache entity and the later write wins. A
  // cache-watching baseline therefore turned into the scenario's own rows
  // (a sold player tagged SELL, a scenario salary edit) the moment the
  // scenario read landed — and writing this read into the cache would do the
  // same to the scenario rows in reverse. `no-cache` neither writes the
  // entities nor watches them. Its cost is one request each time a scenario
  // is opened from Current Squad (the skip flips): not per render, and not on
  // a scenario-to-scenario switch, where the variables do not change.
  const { data: baselineData } = useSquadPlanningGetSquadPlayersByPositionQuery(
    {
      variables: { scenarioId: null },
      fetchPolicy: "no-cache",
      skip: skip || activeId == null,
    }
  );
  const baselineResult = baselineData?.getSquadPlayersByPosition;
  const baselinePlayers: readonly SquadPlayer[] | null = useMemo(() => {
    if (activeId == null) return players;
    if (baselineResult == null) return null;
    return [
      ...baselineResult.firstTeam.map(formatFirstTeamPlayer),
      ...baselineResult.academy.map(formatAcademyPlayer),
    ];
  }, [activeId, players, baselineResult]);

  const hasAnyData = result !== undefined;

  // Full loading is reserved for the true initial state: no data from any
  // scenario yet. Also surfaced while team selection hydrates from
  // AsyncStorage, otherwise consumers see loading: false with empty data
  // for a frame because Apollo reports loading: false while the query is
  // skipped.
  const isLoading = !isTeamSelectionLoaded || (loading && !hasAnyData);

  // Variable change (scenario switch) with previous rows still on screen.
  const isSwitching =
    loading && hasAnyData && networkStatus === NetworkStatus.setVariables;

  // Memoized so a Provider re-render triggered by something unrelated (a
  // sibling context, an unrelated store tick) does not force every one of
  // the roughly 12 consumers below to re-render off a fresh object identity
  // - the exact fan-out this module exists to eliminate.
  const value: SquadPlanningPlayersContextValue = useMemo(
    () => ({
      players,
      rawTargets,
      baselinePlayers,
      loading: isLoading,
      isSwitching,
      error: error ?? undefined,
      refetch: () => {
        void refetch();
      },
    }),
    [
      players,
      rawTargets,
      baselinePlayers,
      isLoading,
      isSwitching,
      error,
      refetch,
    ]
  );

  return (
    <SquadPlanningPlayersContext.Provider value={value}>
      {children}
    </SquadPlanningPlayersContext.Provider>
  );
}

/**
 * Reads the shared squad-planning player list from context.
 *
 * Every call site gets the same already-computed players array reference
 * from the single SquadPlanningPlayersProvider instance above it. Throws
 * when called outside that provider: a silent fallback would let a new
 * call site quietly reintroduce its own independent observer, exactly the
 * SE-8191 fan-out this module exists to prevent.
 * @returns Players, loading state, error, and refetch function
 */
export function useSquadPlanningPlayersContext(): SquadPlanningPlayersContextValue {
  const ctx = useContext(SquadPlanningPlayersContext);
  if (!ctx) {
    throw new Error(
      "useSquadPlanningPlayers must be called beneath SquadPlanningPlayersProvider " +
        "(mounted once in SquadScreenContainer). See contexts/SquadPlanningPlayersContext " +
        "for why this hook no longer runs its own independent Apollo query."
    );
  }
  return ctx;
}

export { SquadPlanningPlayersContext };
